import { Box, Chip, Skeleton, Tooltip, Typography } from '@mui/material'
import { useTemplateForDeal } from 'api/PandaDoc/useTemplateForDeal'
import type { NewDeal } from 'types'

/**
 * Which PandaDoc template this deal's contract uses.
 *
 * Shown because it was previously invisible. A template override lives on the
 * organization record and is applied silently at contract creation, so the only
 * way to find out which paper a client had signed was to open the signed
 * document and read it. That is how eight Zimmer Properties accounts went months
 * on the standard terms without anyone noticing.
 *
 * The distinction that earns the space is `source`. Most deals use the house
 * default and say so quietly. A deal on an override says so in colour, because
 * an override is the case somebody needs to verify before sending.
 */

interface TemplateUsedProps {
  deal?: NewDeal | null
}

const TemplateUsed = ({ deal }: TemplateUsedProps) => {
  const { data, isLoading, isError } = useTemplateForDeal(deal?.PK, deal?.deal_uuid)

  if (!deal?.PK || !deal?.deal_uuid) return null

  if (isLoading) {
    return <Skeleton variant="rounded" width={190} height={20} sx={{ borderRadius: 1 }} />
  }

  // A label nobody can read is worse than no label, but a silent failure here
  // would put us back to the state this component exists to fix. Say it failed.
  if (isError || !data) {
    return (
      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
        Template unavailable
      </Typography>
    )
  }

  if (!data.templateId) {
    return (
      <Tooltip
        title={`No PandaDoc template is mapped for the type "${data.type ?? 'unknown'}"${
          data.mode === 'noSignature' ? ' as a no-signature agreement' : ''
        }. A contract cannot be created until one is.`}
      >
        <Chip
          label="No template mapped"
          size="small"
          color="error"
          variant="outlined"
          sx={{ height: 20, fontSize: 10.5, fontWeight: 700 }}
        />
      </Tooltip>
    )
  }

  const tooltip = data.isOverride
    ? `Set on the ${data.source} rather than the house default. Template id ${data.templateId}.`
    : `The default for ${data.type}${
        data.mode === 'noSignature' ? ' (no signature)' : ''
      }. Template id ${data.templateId}.`

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>
        Template used
      </Typography>
      <Tooltip title={tooltip} enterDelay={400}>
        <Chip
          label={data.templateName ?? data.templateId}
          size="small"
          color={data.isOverride ? 'warning' : 'default'}
          variant="outlined"
          sx={{
            height: 20,
            fontSize: 10.5,
            fontWeight: data.isOverride ? 700 : 500,
            maxWidth: 320,
            '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
          }}
        />
      </Tooltip>
    </Box>
  )
}

export default TemplateUsed
